import React, { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Mail, MessageSquare, Send, Sparkles, Loader2, ArrowDownLeft, ArrowUpRight, StickyNote, UserCheck,
} from "lucide-react";
import { toast } from "sonner";

const FILTERS = [{ v: "all", l: "All" }, { v: "unread", l: "Unread" }, { v: "mine", l: "Assigned to me" }];

export default function Conversations() {
  const { activeSubId, user } = useAuth();
  const [convos, setConvos] = useState([]);
  const [team, setTeam] = useState([]);
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState(null);
  const [channel, setChannel] = useState("email");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [note, setNote] = useState("");
  const [mode, setMode] = useState("reply"); // reply | note
  const [filter, setFilter] = useState("all");
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  const loadList = useCallback(() => {
    if (!activeSubId) return;
    Promise.all([
      api.get(`/conversations?sub_account_id=${activeSubId}`),
      api.get(`/conversations/team-members?sub_account_id=${activeSubId}`),
    ]).then(([c, t]) => {
      setConvos(c.data); setTeam(t.data); setLoading(false);
      setSelected((prev) => prev || c.data[0]?.contact_id || null);
    });
  }, [activeSubId]);

  useEffect(() => { loadList(); }, [loadList]);

  const loadThread = useCallback((cid, markRead = true) => {
    if (!cid) { setThread(null); return; }
    api.get(`/conversations/contact/${cid}`).then(({ data }) => {
      setThread(data);
      if (markRead && data.messages?.some((m) => m.direction === "inbound" && !m.read)) {
        api.post(`/conversations/contact/${cid}/read`).then(() => loadList()).catch(() => {});
      }
    }).catch(() => { setThread(null); });
  }, [loadList]);

  useEffect(() => { loadThread(selected); }, [selected, loadThread]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [thread]);

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/conversations/contact/${selected}/send`, { channel, subject: subject || null, body });
      if (channel === "email") toast[data.delivery === "sent" ? "success" : "error"](data.delivery === "sent" ? "Email sent" : "Email failed to send");
      else if (channel === "telegram") toast[data.delivery === "sent" ? "success" : "error"](data.delivery === "sent" ? "Telegram sent" : "Telegram failed");
      else toast.info("SMS queued (Twilio not yet connected)");
      setBody(""); setSubject("");
      loadThread(selected, false); loadList();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const saveNote = async () => {
    if (!note.trim()) return;
    setSending(true);
    try {
      await api.post(`/conversations/contact/${selected}/note`, { body: note });
      setNote(""); toast.success("Internal note added");
      loadThread(selected, false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const assign = async (assignee_id) => {
    const id = assignee_id === "none" ? null : assignee_id;
    const { data } = await api.put(`/conversations/contact/${selected}/assign`, { assignee_id: id });
    setThread((t) => ({ ...t, assigned_to: data.assigned_to, assigned_name: data.assigned_name }));
    toast.success(id ? `Assigned to ${data.assigned_name}` : "Unassigned");
    loadList();
  };

  const aiDraft = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post(`/conversations/contact/${selected}/ai-draft`, { channel });
      if (data.draft && data.draft.trim()) { setBody(data.draft); toast.success("AI drafted a reply"); }
      else toast.error("Draft came back empty — try again");
    } catch { toast.error("Could not generate draft"); }
    finally { setDrafting(false); }
  };

  const copyConnect = async () => {
    try {
      const { data } = await api.post(`/telegram/connect-link`, { contact_id: selected });
      await navigator.clipboard?.writeText(data.link);
      toast.success("Telegram connect link copied — send it to the contact");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const filtered = convos.filter((c) => {
    if (filter === "unread") return c.unread > 0;
    if (filter === "mine") return c.assigned_to === user?.id;
    return true;
  });
  const totalUnread = convos.reduce((a, c) => a + (c.unread || 0), 0);

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Omnichannel Inbox</p>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight mt-1">Conversations {totalUnread > 0 && <Badge className="ml-1 align-middle" data-testid="total-unread-badge">{totalUnread} unread</Badge>}</h1>
        </div>
        <div className="flex gap-1.5">
          {FILTERS.map((f) => (
            <Button key={f.v} size="sm" variant={filter === f.v ? "default" : "outline"} onClick={() => setFilter(f.v)} data-testid={`filter-${f.v}`}>{f.l}</Button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-250px)] min-h-[500px]">
        <Card className="bg-card border-border/70 overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-border/60 text-sm font-semibold">Inbox ({filtered.length})</div>
          <div className="flex-1 overflow-y-auto divide-y divide-border/60">
            {filtered.length === 0 && <div className="p-6 text-sm text-muted-foreground text-center" data-testid="conversations-empty">No conversations here.</div>}
            {filtered.map((c) => (
              <button key={c.id} onClick={() => setSelected(c.contact_id)}
                data-testid={`conversation-item-${c.contact_id}`}
                className={`w-full text-left px-4 py-3 hover:bg-secondary/40 transition-colors ${selected === c.contact_id ? "bg-secondary/60" : ""}`}>
                <div className="flex items-center gap-2">
                  <span className={`text-sm truncate flex-1 ${c.unread > 0 ? "font-bold" : "font-medium"}`}>{c.contact_name}</span>
                  {c.unread > 0 && <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center shrink-0" data-testid={`unread-badge-${c.contact_id}`}>{c.unread}</span>}
                  {c.last_channel === "email" ? <Mail className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : c.last_channel === "telegram" ? <Send className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : <MessageSquare className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                </div>
                <p className="text-xs text-muted-foreground truncate mt-0.5">{c.preview}</p>
                {c.assigned_name && <p className="text-[10px] text-primary/80 mt-0.5 flex items-center gap-1"><UserCheck className="w-2.5 h-2.5" />{c.assigned_name}</p>}
              </button>
            ))}
          </div>
        </Card>

        <Card className="lg:col-span-2 bg-card border-border/70 overflow-hidden flex flex-col">
          {!thread ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">Select a conversation</div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-border/60 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-primary/15 text-primary flex items-center justify-center font-semibold">
                  {thread.contact?.name[0]?.toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">{thread.contact?.name}</p>
                  <p className="text-xs text-muted-foreground truncate font-mono">{thread.contact?.email || thread.contact?.phone}</p>
                </div>
                <div className="w-44">
                  <Select value={thread.assigned_to || "none"} onValueChange={assign}>
                    <SelectTrigger className="h-8 text-xs" data-testid="assign-select"><SelectValue placeholder="Assign…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
                {(thread.messages || []).map((m) => {
                  if (m.direction === "internal") {
                    return (
                      <div key={m.id} className="flex justify-center" data-testid={`message-${m.id}`}>
                        <div className="max-w-[85%] rounded-xl px-4 py-2.5 bg-amber-500/10 border border-amber-500/20 text-amber-200/90">
                          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-amber-400/80 mb-1"><StickyNote className="w-3 h-3" />Internal note · {m.author}</div>
                          <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                          <p className="text-[10px] text-amber-200/50 mt-1">{new Date(m.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                    );
                  }
                  const out = m.direction === "outbound";
                  return (
                    <div key={m.id} className={`flex ${out ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
                      <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 ${out ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
                        {m.subject && <p className="text-xs font-semibold mb-1 opacity-90">{m.subject}</p>}
                        <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                        <div className={`flex items-center gap-1.5 mt-1.5 text-[10px] ${out ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                          {out ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownLeft className="w-3 h-3" />}
                          <span className="uppercase">{m.channel}</span>
                          {m.via === "automation" && <Badge variant="secondary" className="text-[9px] px-1 py-0 h-3.5">auto</Badge>}
                          {m.via === "broadcast" && <Badge variant="secondary" className="text-[9px] px-1 py-0 h-3.5">campaign</Badge>}
                          <span>· {new Date(m.created_at).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {(!thread.messages || thread.messages.length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-8">No messages yet. Start the conversation below.</p>
                )}
              </div>

              <div className="border-t border-border/60 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Tabs value={mode} onValueChange={setMode}>
                    <TabsList className="h-8">
                      <TabsTrigger value="reply" className="text-xs gap-1" data-testid="mode-reply"><Send className="w-3 h-3" />Reply</TabsTrigger>
                      <TabsTrigger value="note" className="text-xs gap-1" data-testid="mode-note"><StickyNote className="w-3 h-3" />Internal note</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>

                {mode === "note" ? (
                  <div className="flex gap-2 items-end">
                    <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a private note for your team…" className="min-h-[60px] bg-amber-500/5 border-amber-500/20" data-testid="internal-note-input" />
                    <Button onClick={saveNote} disabled={sending || !note.trim()} variant="outline" className="gap-1.5 h-[60px]" data-testid="save-note-button">
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <StickyNote className="w-4 h-4" />}
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3">
                      <Tabs value={channel} onValueChange={setChannel}>
                        <TabsList className="h-8">
                          <TabsTrigger value="email" className="text-xs gap-1" data-testid="channel-email-tab"><Mail className="w-3 h-3" />Email</TabsTrigger>
                          <TabsTrigger value="telegram" className="text-xs gap-1" data-testid="channel-telegram-tab"><Send className="w-3 h-3" />Telegram</TabsTrigger>
                          <TabsTrigger value="sms" className="text-xs gap-1" data-testid="channel-sms-tab"><MessageSquare className="w-3 h-3" />SMS</TabsTrigger>
                        </TabsList>
                      </Tabs>
                      <Button size="sm" variant="outline" className="ml-auto gap-1.5" onClick={aiDraft} disabled={drafting} data-testid="ai-draft-button">
                        {drafting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 text-primary" />} AI draft
                      </Button>
                    </div>
                    {channel === "email" && (
                      <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className="h-9" data-testid="message-subject-input" />
                    )}
                    <div className="flex gap-2 items-end">
                      <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder={`Type your ${channel}…`} className="min-h-[60px]" data-testid="message-body-input" />
                      <Button onClick={send} disabled={sending || !body.trim()} className="gap-1.5 h-[60px]" data-testid="send-message-button">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </div>
                    {channel === "sms" && <p className="text-xs text-muted-foreground">SMS is wired but Twilio credentials are not yet connected — messages are logged to the thread.</p>}
                    {channel === "telegram" && !thread.contact?.telegram_chat_id && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>This contact hasn't connected Telegram.</span>
                        <button onClick={copyConnect} className="text-primary hover:underline" data-testid="copy-telegram-link">Copy connect link</button>
                      </div>
                    )}
                    {channel === "telegram" && thread.contact?.telegram_chat_id && <p className="text-xs text-emerald-400">Telegram connected — messages send instantly and for free.</p>}
                  </>
                )}
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
